import { cn } from '@sohanemon/utils';
import { Iconify, Portal } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import {
	THEME_DEFINITIONS,
	THEME_MODES,
	type ThemeMode,
} from '@sohanscript/theme';
import { Heading } from '@sohanscript/ui/heading';
import { Motion } from '@sohanscript/ui/motion';
import * as React from 'react';
import { ThemePreview } from './theme-preview';

const themeLabels = {
	'moon-script': m.theme_dark,
	'sun-script': m.theme_light,
	'tokyo-night': m.theme_tokyo_night,
	'catppuccin-mocha': m.theme_catppuccin_mocha,
	'catppuccin-latte': m.theme_catppuccin_latte,
	gruvbox: m.theme_gruvbox,
	'rose-pine-moon': m.theme_rose_pine_moon,
	'rose-pine-dawn': m.theme_rose_pine_dawn,
	'osaka-jade': m.theme_osaka_jade,
	'shades-of-purple': m.theme_shades_of_purple,
	supabase: m.theme_supabase,
	github: m.theme_github,
	'one-dark-pro': m.theme_one_dark_pro,
	auto: m.theme_system,
} satisfies Record<ThemeMode, () => string>;

type ThemePickerProps = {
	mode: ThemeMode;
	open: boolean;
	selectedIndex: number;
	onCancel: () => void;
	onCommit: () => void;
	onSelect: (index: number) => void;
};

export function ThemePicker({
	mode,
	open,
	selectedIndex,
	onCancel,
	onCommit,
	onSelect,
}: ThemePickerProps) {
	const items = THEME_MODES;
	const [firstWordLabel, ...restWordsLabel] = themeLabels[mode]().split(' ');
	const [hasEntered, setHasEntered] = React.useState(false);
	const touchStartX = React.useRef<number | null>(null);
	const lastWheel = React.useRef(0);
	const dialogRef = React.useRef<HTMLDivElement>(null);

	function clampTo(index: number) {
		onSelect(Math.min(items.length - 1, Math.max(0, index)));
	}

	function wrapTo(index: number) {
		onSelect((index + items.length) % items.length);
	}

	React.useEffect(() => {
		if (!open) {
			setHasEntered(false);
			return;
		}
		const frame = requestAnimationFrame(() => setHasEntered(true));
		return () => cancelAnimationFrame(frame);
	}, [open]);

	// NOTE: Keep focus inside the picker lifecycle and prevent the page from moving underneath it.
	React.useEffect(() => {
		if (!open) return;
		const previous = document.activeElement as HTMLElement | null;
		const overflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		dialogRef.current?.focus();
		return () => {
			document.body.style.overflow = overflow;
			previous?.focus?.();
		};
	}, [open]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: wrapTo updates on every render
	React.useEffect(() => {
		if (!open) return;
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === 'Escape') onCancel();
			if (
				event.key === 'Enter' &&
				!(event.target instanceof HTMLElement && event.target.closest('button'))
			)
				onCommit();
			if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
				event.preventDefault();
				wrapTo(selectedIndex + 1);
			}
			if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
				event.preventDefault();
				wrapTo(selectedIndex - 1);
			}
		}
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onCancel, onCommit, open, selectedIndex]);

	if (!open) return null;

	const trackIndex = hasEntered ? selectedIndex : 0;

	return (
		<Portal>
			<section
				tabIndex={-1}
				className="fixed inset-0 z-60 flex flex-col justify-center bg-background text-foreground outline-none overscroll-contain"
				role="dialog"
				aria-modal="true"
				aria-label={m.theme_label()}
			>
				<div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-5 sm:p-8">
					<p className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-foreground">
						~/preferences/theme
					</p>
					<button
						type="button"
						className="pointer-events-auto inline-flex size-10 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
						onClick={onCancel}
						aria-label={m.ui_close()}
					>
						<Iconify icon="lucide:x" className="size-5" />
					</button>
				</div>

				<main
					className={cn(
						'relative -skew-x-5 h-[calc(var(--theme-picker-active-height)+2.5rem)] overflow-hidden',
						'[--theme-picker-aspect-ratio:1.55] [--theme-picker-active-width:min(84vw,52rem,calc(42dvh*var(--theme-picker-aspect-ratio)))] [--theme-picker-active-height:calc(var(--theme-picker-active-width)/var(--theme-picker-aspect-ratio))] [--theme-picker-item-step:clamp(2.75rem,4vw,6rem)]',
						'max-sm:[--theme-picker-active-width:50vw] max-sm:[--theme-picker-item-step:1.5rem]',
					)}
					onWheel={(event) => {
						const now = performance.now();
						if (now - lastWheel.current < 140) return;
						const distance =
							Math.abs(event.deltaX) > Math.abs(event.deltaY)
								? event.deltaX
								: event.deltaY;
						if (!distance) return;
						lastWheel.current = now;
						clampTo(selectedIndex + (distance > 0 ? 1 : -1));
					}}
					onTouchStart={(event) => {
						touchStartX.current = event.touches[0]?.clientX ?? null;
					}}
					onTouchEnd={(event) => {
						const startX = touchStartX.current;
						touchStartX.current = null;
						const endX = event.changedTouches[0]?.clientX;
						if (startX === null || endX === undefined) return;
						const distance = endX - startX;
						if (Math.abs(distance) > 40)
							clampTo(selectedIndex + (distance < 0 ? 1 : -1));
					}}
				>
					<div
						className="absolute top-1/2 left-1/2 flex w-max touch-pan-y items-center transition-transform duration-300 ease-out motion-reduce:transition-none"
						style={{
							height: 'var(--theme-picker-active-height)',
							transform: `translate(calc(-1 * (${trackIndex} * var(--theme-picker-item-step) + var(--theme-picker-active-width) / 2)), -50%)`,
						}}
					>
						{items.map((item, index) => {
							const itemLabel = themeLabels[item]();
							const isActive = hasEntered && index === selectedIndex;
							return (
								<Motion
									as="button"
									feature="domMax"
									animate={{}}
									key={item}
									type="button"
									layout
									transition={{
										layout: { duration: 0.4, ease: 'easeInOut' },
									}}
									aria-label={itemLabel}
									aria-current={isActive}
									data-active={isActive}
									onClick={() => (isActive ? onCommit() : wrapTo(index))}
									className={cn(
										'group relative h-[calc(var(--theme-picker-active-height)*0.9)] w-(--theme-picker-item-step) shrink-0 bg-transparent p-0',
										'focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2',
										isActive &&
											'z-2 h-(--theme-picker-active-height) w-(--theme-picker-active-width)',
									)}
								>
									<div
										className={cn(
											'relative h-full w-full overflow-hidden',
											isActive &&
												'border-4 border-primary shadow-[0_0_40px_color-mix(in_srgb,var(--primary)_18%,transparent)]',
										)}
										style={{
											background: THEME_DEFINITIONS[item].preview.background,
										}}
									>
										{/* NOTE: Center the fixed canvas so inactive cards reveal their middle. */}
										<div
											className={cn(
												'absolute top-1/2 left-1/2 h-(--theme-picker-active-height) w-(--theme-picker-active-width) -translate-x-1/2 -translate-y-1/2',
												'group-data-[active=true]:brightness-100 brightness-80 group-hover:brightness-100 transition-[filter] duration-300 ease-out',
											)}
										>
											<ThemePreview mode={item} isActive={isActive} />
										</div>
									</div>
								</Motion>
							);
						})}
					</div>
				</main>

				<footer className="flex flex-col items-center gap-2 px-5 text-center">
					<div className="flex items-center gap-2">
						<Iconify
							icon={THEME_DEFINITIONS[mode].icon}
							className="size-7 text-primary"
						/>
						<Heading
							titlePrefix={firstWordLabel}
							titleHighlight={restWordsLabel.join(' ')}
						/>
					</div>
					<p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-muted-foreground">
						{m.theme_picker_hint()}
					</p>
				</footer>
			</section>
		</Portal>
	);
}
